// Content + media data for Brazier Building & Design

export const BRAND = {
    name: "Brazier Building & Design",
    legal: "Brazier Developments Pty Ltd",
    short: "Brazier Building & Design",
    tagline: "Custom Homes, Renovations & Extensions across the Central Coast & Newcastle",
    email: "info@brazierdevelopments.com.au",
    abn: "54 126 105 191",
    licence: "317424c",
    licenceLabel: "Licence 317424c",
    hours: "Mon–Fri 7:00am – 4:00pm · Weekends by appointment",
    instagram: "https://www.instagram.com/brazierbuildingdesign/",
    facebook: "https://www.facebook.com/wordpress.slider.revolution",
    logoUrl:
        "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/njzap733_unnamed.jpg",
    logoUrlFooter:
        "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/rc24lupd_logo-nobg.png",
};

export const NAV_LINKS = [
    { label: "Services", to: "/#services", id: "services" },
    { label: "Projects", to: "/projects" },
    { label: "Who We Are", to: "/who-we-are" },
    { label: "Blog", to: "/blog" },
    { label: "Contact Us", to: "/contact" },
];

export const BLOG_POSTS = [
    {
        slug: "cheapest-quote-never-cheapest-option",
        category: "Advice",
        title: "The Cheapest Quote Is Never the Cheapest Option",
        subtitle: "What a Knock Down Rebuild on the Central Coast Taught Me",
        author: "Justin Brazier",
        authorRole: "Director & Licensed Builder",
        date: "June 2026",
        dateISO: "2026-06-15",
        readTime: "4 min read",
        cover: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/4s1fcb8b_COverPoint.jpg",
        coverAlt: "Contemporary custom home in Bateau Bay by Brazier Building & Design",
        excerpt:
            "Building costs keep rising. I understand why you shop around for quotes. Getting multiple quotes is smart. Going with the cheapest one is where things go wrong.",
        seoDescription:
            "The cheapest building quote is never the cheapest option. A Central Coast builder's real story of a $60,000 knock down rebuild lesson — and how to compare builder quotes properly.",
        content: [
            { type: "p", text: "Building costs keep rising. I understand why you shop around for quotes. I expect you to. Getting multiple quotes is smart. Going with the cheapest one is where things go wrong." },
            { type: "p", text: "I want to share a story from my own experience as a builder on the Central Coast NSW. I share it because I think it will save you money, time and stress." },
            { type: "h2", text: "The $60,000 Lesson" },
            { type: "p", text: "A couple of years ago I worked closely with a client planning a knock down rebuild on the Central Coast. We spent weeks together refining her plans. She was honest with me. She said she wanted to build with me but my quote was $60,000 over her budget. She went with a cheaper builder. I respected her decision then and I respect it now." },
            { type: "p", text: "Two years later, her house was finished." },
            { type: "quote", text: "Two years. For a 228 square metre home. A build like hers takes 5 to 6 months with the right builder." },
            { type: "p", text: "Here is the part nobody talks about. While she waited, she paid rent. Over those two years, her rent bill matched the $60,000 she saved on the quote. The saving vanished. She also carried two years of stress, delays and uncertainty. I would not wish her experience on anyone." },
            {
                type: "image",
                src: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/3x0vbkh0_COverBrowV3.jpg",
                alt: "Custom home in Wamberal by Brazier Building & Design",
                caption: "A custom home in Wamberal — completed on time, with the builder on site from start to finish.",
            },
            { type: "h2", text: "Why Cheap Quotes Cost More" },
            { type: "p", text: "In my experience, some builders price low to win the job. Then the variations start. Nine times out of ten, the cheap quote leaves things out. You find out mid-build, when walking away is no longer an option." },
            { type: "p", text: "Variations happen on every project. Clients change their minds. You start shopping for tiles and finishes, fall in love with something beautiful, and your budget slips. This is normal. A good builder plans for it and talks you through it upfront. A cheap builder counts on it." },
            { type: "h2", text: "What the Real Cost of a Build Includes" },
            { type: "p", text: "Your budget is bigger than the contract price. When comparing quotes for a custom home or knock down rebuild, add up:" },
            {
                type: "list",
                items: [
                    "Rent or holding costs during the build. Every extra month costs you thousands.",
                    "Variations. Ask each builder what triggers one and what is included in the base price.",
                    "Timeline. A 6 month build and a 24 month build are not the same product, even at the same price.",
                    "Stress. Hard to price. Easy to underestimate.",
                ],
            },
            {
                type: "image",
                src: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/1qcnm5n3_brow-image02.jpg",
                alt: "Kitchen interior of a Brazier custom home",
                caption: "Finishes and fixtures are where budgets slip — a good builder walks you through allowances upfront.",
            },
            { type: "h2", text: "How to Choose a Builder on the Central Coast" },
            { type: "p", text: "Do your homework before you sign anything:" },
            {
                type: "list",
                items: [
                    "Investigate the builder. Check completed projects, licences and references.",
                    "Ask what is included in the quote. Line by line. Site costs, finishes, fixtures, allowances.",
                    "Ask about the timeline. Get it in writing. Ask what happens if it blows out.",
                    "Trust your gut. You will work with this person for months. A good feeling matters.",
                ],
            },
            { type: "p", text: "The right [custom home builder on the Central Coast](/who-we-are) will answer every question without hesitation. If a builder dodges your questions on inclusions or timelines, keep looking." },
            { type: "h2", text: "The Bottom Line" },
            { type: "p", text: "The cheapest quote is never the cheapest option. Do your homework, and the dearer quote often becomes the cheapest outcome. Faster build. Fewer surprises. No rent bleeding your savings dry. See the standard we hold ourselves to in [our completed projects](/projects)." },
            { type: "p", text: "Planning a knock down rebuild or custom home on the Central Coast? [Ask me the hard questions](/contact). I welcome them." },
        ],
        faq: [
            {
                q: "How long should a knock down rebuild take on the Central Coast?",
                a: "For a typical 200–250 square metre home, around 5 to 6 months with the right builder. If a builder can't commit to a written timeline — or the job stretches past a year — that's a warning sign.",
            },
            {
                q: "Why do cheap building quotes end up costing more?",
                a: "Cheap quotes usually leave things out. You discover the gaps mid-build as variations, when walking away is no longer an option. Add rent or holding costs during a delayed build and the saving disappears quickly.",
            },
            {
                q: "What should I check before choosing a builder?",
                a: "Check completed projects, licences and references. Ask what's included in the quote line by line — site costs, finishes, fixtures and allowances. Get the timeline in writing, and ask what happens if it blows out.",
            },
        ],
    },
];

export const SERVICES = [
    {
        slug: "custom-homes",
        title: "Custom Homes",
        blurb:
            "Architect-led custom homes built to your block, your brief and your way of living.",
        icon: "Home",
    },
    {
        slug: "renovations",
        title: "Renovations",
        blurb:
            "Whole-home renovations and targeted refurbishments — including the option to stay on site to save accommodation costs.",
        icon: "Hammer",
    },
    {
        slug: "extensions",
        title: "Extensions",
        blurb:
            "Thoughtful additions that extend your home's footprint without losing the character you love.",
        icon: "Maximize2",
    },
    {
        slug: "knockdown-rebuilds",
        title: "Knockdown Rebuilds",
        blurb:
            "A new home on the block you already love — from demolition through to handover.",
        icon: "Building2",
    },
    {
        slug: "granny-flats",
        title: "Granny Flats",
        blurb:
            "Compact secondary dwellings — perfect for family, guests or rental income.",
        icon: "House",
    },
];

export const HERO_IMAGE =
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=80";

export const HERO_SLIDES = [
    {
        url: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/e8ag2s51_CoverNAV4.jpg",
        alt: "North Avoca — coastal renovation by Brazier Building & Design",
    },
    {
        url: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/afbo3etj_Cover.jpg",
        alt: "Lowanna Ave — Forresters Beach renovation by Brazier Building & Design",
    },
    {
        url: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/hxpdlyvu_Cover-valleyview.jpg",
        alt: "Valley View — Bateau Bay renovation by Brazier Building & Design",
    },
    {
        url: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/3x0vbkh0_COverBrowV3.jpg",
        alt: "Wamberal — custom home by Brazier Building & Design",
    },
    {
        url: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/948dewrl_yakl_cover.jpg",
        alt: "Forresters Beach Extension — Brazier Building & Design",
    },
    {
        url: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/4s1fcb8b_COverPoint.jpg",
        alt: "Bateau Bay Twentynine — custom home by Brazier Building & Design",
    },
];

export const ABOUT_IMAGE =
    "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/aykfh7ur_image03a.jpg";

export const VIDEO_POSTER =
    "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=2000&q=80";

// Video slider sources. Supports both image (Ken Burns) and mp4 (video) entries.
export const VIDEO_SLIDES = [
    { type: "vimeo", id: "1200985923" },
];

export const GALLERY_STRIP = {
    tall: {
        url: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/o57immjc_image05a.jpg",
        urlMobile: "/images/gallery-tall-mobile.jpg",
        alt: "Brazier Building & Design — coastal interior with timber floors and skylight",
    },
    detail: {
        url: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/afbo3etj_Cover.jpg",
        alt: "Brazier Building & Design — Forresters Beach coastal home",
    },
};

export const REVIEWS = [
    {
        name: "Charlie Powell",
        suburb: "5 months ago",
        rating: 5,
        text:
            "Brazier Building & Design have just complex our home at North Avoca. Outstanding. Justin's manner, humour, knowledge and expertise were on display through out the whole build. We changed our minds and had variations but Brazier Building & Design was always understanding and quick to let us know the positives and negatives and if it was doable along with prompt pricing. We just couldn't be happier with Justin and the team from Brazier Building and Design. Thank you Justin.",
    },
    {
        name: "Simon Leahy",
        suburb: "a year ago",
        rating: 5,
        text:
            "We couldn't be happier with our experience working with Brazier Building on our house renovation. From start to finish, their communication was clear, responsive, and professional. The team maintained an exceptionally clean and organised site throughout the entire project, which gave us a lot of confidence in how things were being run. The quality of finish is outstanding — every detail has been executed to a high standard. Brazier Building also worked seamlessly with all other trades and certifiers involved, ensuring the entire process was smooth and well-coordinated. Highly recommend them to anyone looking for a reliable, high-quality builder!",
    },
    {
        name: "Alli Foskett",
        suburb: "a year ago",
        rating: 5,
        text:
            "I couldn't be happier with the renovation work completed by Justin and his team at Brazier Building. From the beginning, they truly listened and understood my vision, bringing all my ideas to life while carefully preserving the character and charm of the original home. I had some unique and ambitious concepts, but nothing was ever too much or too out there. They approached everything with a \"can-do\" attitude and made it all happen with incredible attention to detail. Communication was always clear and consistent, making the whole process smooth and stress-free. I'll definitely be using them again for any future developments. Highly recommended!",
    },
    {
        name: "Matt Stanton",
        suburb: "11 months ago",
        rating: 5,
        text:
            "Words cannot express how happy are with Justin and his team. From our very first meeting until the completion of our home JB was professional, honest and accommodating. We couldn't have picked a more difficult time to build with Covid and bad weather, but JB finished the project on time and on budget. You will not be disappointed. Thanks again to the boys and girls at Brazier.",
    },
    {
        name: "Ian Foscholo",
        suburb: "a year ago",
        rating: 5,
        text:
            "Our build was undertaken during the height of covid. Justin and his team launched into our construction and completed the work in a very professional manner. As the work progressed, I was increasingly confident in his ability to organise and meet our building requirements and in some cases adjusted our building specifications on his recommendations. Highly recommended.",
    },
];

export const AWARDS = [
    { label: "HIA Member", icon: "Award" },
    { label: `Licensed Builder ${"317424c"}`, icon: "ShieldCheck" },
    { label: "Fully Insured", icon: "BadgeCheck" },
    { label: "30+ Years Experience", icon: "Medal" },
];

export const PARTNERS = [
    "Reece Plumbing",
    "HIA",
    "Mitre 10",
    "James Hardie",
    "Colorbond",
    "Bosch",
    "Caroma",
    "Miele",
    "Adoro Cor Design",
];

export const PROJECTS = [
    {
        id: "lowanna-ave",
        name: "Lowanna Ave",
        type: "Renovations",
        suburb: "Forresters Beach",
        year: "2024",
        cover: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/afbo3etj_Cover.jpg",
        description:
            "A full renovation of a beachside family home — re-imagining the floor plan, opening living spaces to the garden and introducing a refined, coastal palette throughout.",
        gallery: [
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/afbo3etj_Cover.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/w2yaqnss_VV-image01a.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/dzsywfr3_Image01.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/m08keq0i_Image03.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/pl71xae8_Image02.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/w7n7fckq_Image04.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/v0p1ygwj_Image05.jpg",
        ],
    },
    {
        id: "north-avoca",
        name: "North Avoca",
        type: "Renovations",
        suburb: "North Avoca",
        cover: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/e8ag2s51_CoverNAV4.jpg",
        description:
            "A sensitive renovation of a much-loved coastal cottage — white weatherboard, gable roofline, timber deck, frangipani-shaded entry and a carport addition tied seamlessly into the existing form.",
        gallery: [
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/7o25x2xt_VV-image07.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/hlazrhln_VV-image013.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/nwf4nzeh_VV-image012.remini-enhanced.jpg",
        ],
    },
    {
        id: "valley-view",
        name: "Valley View",
        type: "Renovations",
        suburb: "Bateau Bay",
        year: "2024",
        cover: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/hxpdlyvu_Cover-valleyview.jpg",
        video: { provider: "vimeo", id: "1194349206" },
        description:
            "A sensitive renovation of a much-loved coastal home — opening the rear to the garden, refreshing the kitchen and adding a generous covered alfresco.",
        gallery: [
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/und3j51o_VV-image01a.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/4cotljp0_VV-image02.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/99d4sl13_VV-image03.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/0q88757l_VV-image04.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/qze5o1hu_VV-image05.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/sl4ojk91_valley_image01.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/4u0zfetg_valley_image02.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/1s330rmk_valley_image03.jpg",
        ],
    },
    {
        id: "wamberal",
        name: "Wamberal",
        type: "Custom Homes",
        suburb: "Wamberal",
        year: "2023",
        cover: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/3x0vbkh0_COverBrowV3.jpg",
        description:
            "A custom new home in Wamberal designed around northerly aspect and indoor/outdoor living — vertical batten timber entry, soft grey kitchen joinery, oak floors and a generous covered alfresco for entertaining.",
        gallery: [
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/3x0vbkh0_COverBrowV3.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/va3h20zz_COverBrow.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/1qcnm5n3_brow-image02.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/hk1okecl_brow-image03.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/fyfjmgi3_brow-image01.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/18fvrsot_brow-image04.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/wck8tu2r_brow-image05.jpg",
        ],
    },
    {
        id: "forresters-beach-extension",
        name: "Forresters Beach Extension",
        type: "Alterations and Additions",
        suburb: "Forresters Beach",
        year: "2023",
        cover: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/948dewrl_yakl_cover.jpg",
        description:
            "A coastal extension that added a new living wing and poolside connection — white-painted brickwork, glass balustrades, hardwood decking and tropical landscaping.",
        gallery: [
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/948dewrl_yakl_cover.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/p2zv48w5_Yak_Image01.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/6skgsxkh_Yak_Image02.jpg",
        ],
    },
    {
        id: "bateau-bay-twentynine",
        name: "Bateau Bay",
        type: "Custom Homes",
        suburb: "Bateau Bay",
        year: "",
        cover: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/4s1fcb8b_COverPoint.jpg",
        description:
            "A contemporary custom home with sculpted rooflines, dry-stack stone walls and corten signage — designed to make a quiet but confident street address.",
        gallery: [
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/4s1fcb8b_COverPoint.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/n21i2mj2_VV-image01a.remini-enhanced.jpg",
            "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/95h4aefb_VV-image02.remini-enhanced.jpg",
        ],
    },
];

export const PROJECT_FILTERS = ["All", "Custom Homes", "Renovations", "Alterations and Additions", "Knockdown Rebuilds"];

export const INSPIRATIONS = [
    { category: "Kitchens", url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=900&q=80" },
    { category: "Kitchens", url: "https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?auto=format&fit=crop&w=900&q=80" },
    { category: "Kitchens", url: "https://images.unsplash.com/photo-1565538810643-b5bdb714032a?auto=format&fit=crop&w=900&q=80" },
    { category: "Kitchens", url: "https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?auto=format&fit=crop&w=900&q=80" },
    { category: "Bathrooms", url: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=900&q=80" },
    { category: "Bathrooms", url: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=900&q=80" },
    { category: "Bathrooms", url: "https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=900&q=80" },
    { category: "Bathrooms", url: "https://images.unsplash.com/photo-1552322629-aa196ce969b5?auto=format&fit=crop&w=900&q=80" },
    { category: "Exteriors", url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=900&q=80" },
    { category: "Exteriors", url: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=900&q=80" },
    { category: "Exteriors", url: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=900&q=80" },
    { category: "Exteriors", url: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=900&q=80" },
    { category: "Living Areas", url: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=900&q=80" },
    { category: "Living Areas", url: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=900&q=80" },
    { category: "Living Areas", url: "https://images.unsplash.com/photo-1600210492493-0946911123ea?auto=format&fit=crop&w=900&q=80" },
    { category: "Living Areas", url: "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=900&q=80" },
];

export const INSPIRATION_FILTERS = ["All", "Kitchens", "Bathrooms", "Exteriors", "Living Areas"];

export const TEAM = [
    {
        name: "Justin Brazier",
        role: "Director & Licensed Builder",
        image: "https://customer-assets.emergentagent.com/job_web-project-300/artifacts/aykfh7ur_image03a.jpg",
    },
    {
        name: "Lynne Hardy",
        role: "Interior Design · Adoro Cor Design",
        image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=900&q=80",
    },
];

export const VALUES = [
    {
        title: "Experienced",
        blurb:
            "Over 30 years of hands-on experience with direct builder involvement on every project — start to finish.",
    },
    {
        title: "Refined",
        blurb:
            "A focus on doing things properly. High standard of finish, clean detailing and a quiet, considered aesthetic.",
    },
    {
        title: "Honest",
        blurb:
            "Clear communication, transparent pricing and no surprises. We let the work speak for itself.",
    },
];
